import { useMemo } from 'react';
import { ReactionType } from '../../../firebase/firebase.types';
import useHover from '../../../hooks/useHover';
import { useAppSelector } from '../../../redux/hooks';
import { TripCard } from '../../../redux/trip/trip.types';
import { selectCurrentUser } from '../../../redux/user/user.selectors';
import { getReactionCounts, getReactionEmoji } from '../../../utils/reaction-utils';
import ReactionTooltip from './ReactionTooltip';
import './ReactionCount.scss';

interface ReactionCountProps {
  card: TripCard;
  isLarge?: boolean;
  isHovered?: boolean;
  isMobile?: boolean;
  isInline?: boolean; // New prop for inline positioning
  isItinerarySidebarCard?: boolean; // New prop to disable hover animations
}

function ReactionCount({
  card,
  isLarge = false,
  isHovered = false,
  isMobile = false,
  isInline = false,
  isItinerarySidebarCard = false,
}: ReactionCountProps) {
  const currentUser = useAppSelector(selectCurrentUser);
  const { isHovered: showTooltip, hoverProps } = useHover();

  // Calculate reaction counts
  const reactionCounts = useMemo(() => getReactionCounts(card), [card]);

  // Check if any reactions exist
  const hasReactions = useMemo(() => {
    return Object.values(reactionCounts).some((count) => count > 0);
  }, [reactionCounts]);

  // Get the user's current reaction
  const userCurrentReaction = useMemo(() => {
    return (currentUser?.uid && card.reactions?.[currentUser.uid]) || null;
  }, [card.reactions, currentUser?.uid]);

  // Don't render if no reactions
  if (!hasReactions) {
    return null;
  }

  // Show only reactions with counts greater than 0
  const visibleReactions = Object.entries(reactionCounts)
    .filter(([_, count]) => count > 0)
    .map(([type, count]) => ({
      type: parseInt(type) as ReactionType,
      count,
    }));

  const containerClassName = [
    'reaction-count-container',
    isLarge ? ' large' : '',
    isInline ? ' inline' : '',
    isMobile ? ' mobile' : '',
    isHovered && !isLarge && !isInline && !isItinerarySidebarCard ? ' hovered' : '',
    isItinerarySidebarCard ? ' itinerary-sidebar' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={containerClassName}
      {...hoverProps}
    >
      {visibleReactions.map(({ type, count }) => {
        const isUserReaction = userCurrentReaction?.type === type;
        return (
          <div
            className='reaction-item'
            key={type}
          >
            <span className='reaction-emoji'>{getReactionEmoji(type)}</span>
            <span className={`reaction-number${isUserReaction ? ' bold' : ''}`}>{count}</span>
          </div>
        );
      })}
      <ReactionTooltip
        card={card}
        isVisible={showTooltip}
        isLarge={isLarge}
        currentUser={currentUser}
      />
    </div>
  );
}

ReactionCount.displayName = 'ReactionCount';
export default ReactionCount;
